import { Controller } from '@hotwired/stimulus'

// Acordeón + progress bar para la Pantalla 1 de subida de
// documentación de hipotecas (ENH-RNK-025 A-15, prototipo Naiara).
//
// Responsabilidades:
//   - Toggle abrir/cerrar items individuales del acordeón.
//   - Recalcular la barra de progreso global (X de N archivos listos)
//     escuchando los cambios en los inputs file de cada item.
//
// Targets:
//   - item: cada item del acordeón (clickable header + body)
//   - progressFill: la franja de la barra de progreso
//   - progressCounter: el span con el texto "X de N archivos listos"
//   - cotitularCampo/cotitularNombre/cotitularEmail: datos del 2º titular
//     (firmante 2 de la LOPD), visibles solo con 2 titulares
//
// Values:
//   - requiredCount: número de campos obligatorios POR titular (4)
//
// El controller no conoce el state de tabs/titulares (eso vive en
// hipotecas-doc-titulares). Para el progress simplemente cuenta los
// inputs file con `[data-required="true"]` que tengan archivos.
export default class extends Controller {
  static targets = [
    'item', 'progressFill', 'progressCounter',
    'cotitularCampo', 'cotitularNombre', 'cotitularEmail', 'cotitularDni', 'cotitularTelefono',
    'lopdDescarga', 'lopdSubir', 'lopdManual'
  ]
  static values = {
    requiredCount: { type: Number, default: 4 }
  }

  connect () {
    this.handleInputChangeBound = this.handleInputChange.bind(this)
    this.handleTitularesChangeBound = this.handleTitularesChange.bind(this)
    this.handleTabChangeBound = this.handleTabChange.bind(this)
    this.element.addEventListener('change', this.handleInputChangeBound)
    // Cuando el stepper de titulares cambia número de titulares o el tab activo,
    // los inputs file relevantes cambian → refrescamos badges y progress. Sin esto,
    // un T2 recién creado mostraría el badge "✓ 1 archivo" copiado del T1 oculto.
    this.element.addEventListener('hipotecas-doc-titulares:titulares-changed', this.handleTitularesChangeBound)
    this.element.addEventListener('hipotecas-doc-titulares:tab-changed', this.handleTabChangeBound)
    // Cuando la firma online queda registrada, la vía manual (descargar + subir a
    // mano) deja de tener sentido: la ocultamos para que no quede el dropzone
    // abierto junto al "✓ Firma registrada" (feedback Carla jul 2026). Desde que
    // el usuario termina de firmar, sin esperar al cierre de Firmafy (Naiara,
    // 5-oct-2026); vuelve si la firma no llega a confirmarse.
    this.handleFirmaOnlineBound = this.handleFirmaOnline.bind(this)
    this.handleFirmaRestauradaBound = this.handleFirmaRestaurada.bind(this)
    this.element.addEventListener('hipotecas-lopd-firma-online:recibida', this.handleFirmaOnlineBound)
    this.element.addEventListener('hipotecas-lopd-firma-online:firmada', this.handleFirmaOnlineBound)
    this.element.addEventListener('hipotecas-lopd-firma-online:restaurada', this.handleFirmaRestauradaBound)
    this.actualizarTodosLosItems()
    this.actualizarProgress()
    this.actualizarCotitular()
    this.actualizarDescargaLopd()
  }

  disconnect () {
    if (this.handleInputChangeBound) this.element.removeEventListener('change', this.handleInputChangeBound)
    if (this.handleTitularesChangeBound) this.element.removeEventListener('hipotecas-doc-titulares:titulares-changed', this.handleTitularesChangeBound)
    if (this.handleTabChangeBound) this.element.removeEventListener('hipotecas-doc-titulares:tab-changed', this.handleTabChangeBound)
    if (this.handleFirmaOnlineBound) {
      this.element.removeEventListener('hipotecas-lopd-firma-online:recibida', this.handleFirmaOnlineBound)
      this.element.removeEventListener('hipotecas-lopd-firma-online:firmada', this.handleFirmaOnlineBound)
    }
    if (this.handleFirmaRestauradaBound) {
      this.element.removeEventListener('hipotecas-lopd-firma-online:restaurada', this.handleFirmaRestauradaBound)
    }
  }

  handleFirmaOnline () {
    if (this.hasLopdManualTarget) this.lopdManualTarget.hidden = true
  }

  handleFirmaRestaurada () {
    if (this.hasLopdManualTarget) this.lopdManualTarget.hidden = false
  }

  handleTitularesChange () {
    this.actualizarTodosLosItems()
    this.actualizarProgress()
    this.actualizarCotitular()
    this.actualizarDescargaLopd()
  }

  // Vía manual de la LOPD según nº de titulares: con 2 titulares el enlace apunta
  // al PDF con los dos titulares y el texto "súbelo…" avisa de que deben firmar
  // todos; con 1 (o 3, sin plantilla propia) al de un titular.
  actualizarDescargaLopd () {
    const dosTitulares = this.numTitularesActuales() >= 2
    if (this.hasLopdDescargaTarget) {
      const link = this.lopdDescargaTarget
      const url = dosTitulares ? link.dataset.urlDosTitulares : link.dataset.urlUnTitular
      if (url) link.href = url
    }
    if (this.hasLopdSubirTarget) {
      const subir = this.lopdSubirTarget
      const texto = dosTitulares ? subir.dataset.textoDosTitulares : subir.dataset.textoUnTitular
      if (texto) subir.textContent = texto
    }
  }

  // Muestra el campo del 2º titular (nombre, email y documento) y lo marca required cuando
  // hay 2 titulares; lo oculta y limpia con 1. Si el bloque no existe (brokers,
  // o canal sin LOPD) no hace nada.
  actualizarCotitular () {
    if (!this.hasCotitularCampoTarget) return
    const dosTitulares = this.numTitularesActuales() >= 2
    this.cotitularCampoTarget.hidden = !dosTitulares

    const inputs = []
    if (this.hasCotitularNombreTarget) inputs.push(this.cotitularNombreTarget)
    if (this.hasCotitularEmailTarget) inputs.push(this.cotitularEmailTarget)
    inputs.forEach(input => {
      input.required = dosTitulares
      if (!dosTitulares) input.value = ''
    })

    // Documento de identidad del cotitular: obligatorio, el que sea (DNI, NIE,
    // pasaporte…) y sin validar (producto, 7-oct-2026).
    if (this.hasCotitularDniTarget) {
      this.cotitularDniTarget.required = dosTitulares
      if (!dosTitulares) this.cotitularDniTarget.value = ''
    }

    // Teléfono del cotitular: opcional (nunca required), pero se limpia igual
    // al volver a 1 titular para no arrastrar datos fantasma.
    if (this.hasCotitularTelefonoTarget && !dosTitulares) this.cotitularTelefonoTarget.value = ''
  }

  handleTabChange () {
    this.actualizarTodosLosItems()
  }

  toggle (event) {
    const header = event.currentTarget
    const item = header.closest('.rnk-HipotecasDoc_AccItem')
    if (!item) return
    const body = item.querySelector('.rnk-HipotecasDoc_AccBody')
    header.classList.toggle('rnk-HipotecasDoc_AccHeader-open')
    if (body) body.classList.toggle('rnk-HipotecasDoc_AccBody-open')
  }

  handleInputChange (event) {
    if (event.target.matches('input[type="file"]')) {
      this.actualizarItemPorInput(event.target)
      this.actualizarProgress()
    }
  }

  actualizarTodosLosItems () {
    this.itemTargets.forEach(item => this.actualizarItem(item))
  }

  actualizarItemPorInput (input) {
    const item = input.closest('.rnk-HipotecasDoc_AccItem')
    if (item) this.actualizarItem(item)
  }

  // Marca el item como "done" (✓ N archivos) si el input file del titular
  // ACTIVO tiene archivos. Si todos están vacíos, deja el badge
  // OBLIGATORIO/OPCIONAL como esté.
  // OJO: contar inputs de TODOS los bloques [data-titular] daba un bug
  // (Naiara 2026-05-13): subir 1 archivo en T1, pulsar +titular → en T2 se
  // veía "✓ 1 archivo" copiado del T1 oculto. Por eso filtramos por el
  // titular activo (o el item global si no tiene bloques por titular).
  actualizarItem (item) {
    const total = this.contarArchivosVisiblesEn(item)
    const header = item.querySelector('.rnk-HipotecasDoc_AccHeader')
    const status = item.querySelector('.rnk-HipotecasDoc_AccStatus')
    if (!header || !status) return

    const badge = status.querySelector('.rnk-HipotecasDoc_AccBadge')
    let doneEl = status.querySelector('.rnk-HipotecasDoc_AccDoneText')

    if (total > 0) {
      header.classList.add('rnk-HipotecasDoc_AccHeader-done')
      if (badge) badge.hidden = true
      if (!doneEl) {
        doneEl = document.createElement('span')
        doneEl.className = 'rnk-HipotecasDoc_AccDoneText'
        status.insertBefore(doneEl, status.firstChild)
      }
      // El check ya no viaja en el texto: lo pinta el ::before de
      // .rnk-HipotecasDoc_AccDoneText con el icono Lucide (ENH-RNK-1855).
      // Los documentos por tipo muestran el recuento; un item con
      // data-done-text (p. ej. la LOPD firmada a mano) fija su propio texto
      // de estado ("Documento firmado"), donde el recuento no aporta.
      const doneText = item.dataset.doneText
      doneEl.textContent = doneText || (total === 1 ? '1 archivo' : `${total} archivos`)
      doneEl.hidden = false
    } else {
      header.classList.remove('rnk-HipotecasDoc_AccHeader-done')
      if (doneEl) doneEl.hidden = true
      if (badge) badge.hidden = false
    }
  }

  // Progress: cuenta items REQUIRED que tienen al menos un archivo en
  // alguno de sus inputs file. Total = requiredCount × numTitulares.
  actualizarProgress () {
    if (!this.hasProgressFillTarget && !this.hasProgressCounterTarget) return

    const numTitulares = this.numTitularesActuales()
    const total = this.requiredCountValue * numTitulares
    let listos = 0

    for (let t = 1; t <= numTitulares; t += 1) {
      this.itemTargets.forEach(item => {
        if (item.dataset.required !== 'true') return
        const inputT = item.querySelector(`[data-titular="${t}"] input[type="file"]`)
        if (!inputT) return
        if (inputT.files && inputT.files.length > 0) listos += 1
      })
    }

    const pct = total === 0 ? 0 : (listos / total) * 100
    if (this.hasProgressFillTarget) this.progressFillTarget.style.width = `${pct}%`
    if (this.hasProgressCounterTarget) {
      this.progressCounterTarget.innerHTML = `<strong>${listos}</strong> de ${total} documentos listos para enviar`
    }
  }

  // Lee el número de titulares del target `contador` directamente.
  // OJO: el código previo buscaba `closest('.rnk-HipotecasDoc').querySelector('[data-controller~="hipotecas-doc-titulares"]')`
  // pero ambos controllers viven en el MISMO root `.rnk-HipotecasDoc`, y
  // `querySelector` solo escanea descendientes (no incluye el propio
  // elemento). Resultado: stepperEl siempre era null → siempre devolvía 1
  // → el counter mostraba "X de 4" aunque hubiera 2-3 titulares
  // (Naiara 2026-05-13). El target `contador` SÍ es descendiente, así
  // que querySelector lo encuentra sin problema.
  numTitularesActuales () {
    const contador = this.element.querySelector('[data-hipotecas-doc-titulares-target="contador"]')
    if (!contador) return 1
    const n = parseInt(contador.textContent, 10)
    return Number.isFinite(n) && n >= 1 ? n : 1
  }

  // Cuenta archivos del titular activo (o globales si el item no tiene
  // bloques [data-titular]).
  contarArchivosVisiblesEn (item) {
    const bloques = item.querySelectorAll('[data-titular]')
    if (bloques.length === 0) {
      // Item global (sin titular): suma todos sus inputs file
      const inputs = item.querySelectorAll('input[type="file"]')
      return Array.from(inputs).reduce((acc, inp) => acc + (inp.files ? inp.files.length : 0), 0)
    }
    let total = 0
    bloques.forEach(bloque => {
      if (bloque.hidden) return
      bloque.querySelectorAll('input[type="file"]').forEach(inp => {
        total += inp.files ? inp.files.length : 0
      })
    })
    return total
  }
}
